import { isEmpty, mapMaybe } from '@execonline-inc/collections';
import { Maybe } from 'maybeasy';
import { includes } from 'ramda';
import { AttributeResource, SupportedLanguageResource } from '../../../../../Types';
import FilterStore from '../FilterTemplate/Store';
import { Filter } from '../FilterTemplate/Types';
import { ExperienceResource, ExperiencesResource, Toggle } from '../Types';

const hasNameText = (competency: AttributeResource): Maybe<string> =>
  competency.payload.name.map((t) => t.text);

const experiencePrimaryCompetencies = (
  experienceResource: ExperienceResource,
): ReadonlyArray<string> => mapMaybe(hasNameText, experienceResource.payload.competencies);

const anyMatchingCompetency = (
  experiencePrimaryCompetencies: ReadonlyArray<string>,
  competencies: ReadonlyArray<string>,
): boolean =>
  experiencePrimaryCompetencies.some((competency) => includes(competency, competencies));

const anyMatchingLanguage = (
  experienceLanguages: ReadonlyArray<SupportedLanguageResource>,
  languages: ReadonlyArray<string>,
): boolean =>
  experienceLanguages.some((language) => includes(language.payload.englishName, languages));

const anyMatchingSchoolPartner = (
  experienceSchoolPartner: string,
  schoolPartners: ReadonlyArray<string>,
): boolean => includes(experienceSchoolPartner, schoolPartners);

const anyMatchingProductType = (
  experienceProductType: string,
  productTypes: ReadonlyArray<string>,
): boolean => includes(experienceProductType, productTypes);

const anyMatchingDuration = (
  experienceDuration: Maybe<number>,
  durations: ReadonlyArray<string>,
): boolean =>
  experienceDuration
    .map((d) => includes(String(d), durations))
    .getOrElse(() => includes(String(0), durations));

const whenAnyMatchingCompetencyOrHasNoCompetencies = (
  competencyFilter: Filter,
  experienceResource: ExperienceResource,
): boolean =>
  isEmpty(competencyFilter.options) ||
  anyMatchingCompetency(
    experiencePrimaryCompetencies(experienceResource),
    competencyFilter.options,
  ) ||
  isEmpty(experienceResource.payload.competencies);

const whenAnyMatchingSchoolPartner = (
  schoolPartnerFilter: Filter,
  experienceResource: ExperienceResource,
): boolean =>
  isEmpty(schoolPartnerFilter.options) ||
  anyMatchingSchoolPartner(
    experienceResource.payload.schoolPartnerName,
    schoolPartnerFilter.options,
  );

const whenAnyMatchingLanguage = (
  languageFilter: Filter,
  experienceResource: ExperienceResource,
): boolean =>
  isEmpty(languageFilter.options) ||
  anyMatchingLanguage(experienceResource.payload.supportedLanguages, languageFilter.options);

const whenAnyMatchingDuration = (
  duration: Filter,
  experienceResource: ExperienceResource,
): boolean =>
  isEmpty(duration.options) ||
  anyMatchingDuration(experienceResource.payload.totalHours, duration.options);

const whenAnyMatchingProductType = (
  productTypeFilter: Filter,
  experienceResource: ExperienceResource,
): boolean =>
  isEmpty(productTypeFilter.options) ||
  anyMatchingProductType(experienceResource.payload.offeringType, productTypeFilter.options);

const whenAnyStatusMatch = (status: Toggle, experienceResource: ExperienceResource) => {
  switch (status) {
    case 'show-licensed':
      return experienceResource.payload.status === 'Licensed';
    case 'show-licensed-and-unlicensed':
      return true;
  }
};

const anyExperienceThatMatchesFilters =
  (
    productTypeFilter: Filter,
    competencyFilter: Filter,
    schoolPartnerFilter: Filter,
    durationFilter: Filter,
    languageFilter: Filter,
    status: Toggle,
  ) =>
  (experience: ExperienceResource): boolean =>
    whenAnyMatchingProductType(productTypeFilter, experience) &&
    whenAnyMatchingCompetencyOrHasNoCompetencies(competencyFilter, experience) &&
    whenAnyMatchingSchoolPartner(schoolPartnerFilter, experience) &&
    whenAnyMatchingDuration(durationFilter, experience) &&
    whenAnyMatchingLanguage(languageFilter, experience) &&
    whenAnyStatusMatch(status, experience);

export const filteredExperiences = (
  experiences: ExperiencesResource,
  productTypeFilterStore: FilterStore,
  competencyFilterStore: FilterStore,
  durationFilterStore: FilterStore,
  schoolPartnerFilterStore: FilterStore,
  languageFilterStore: FilterStore,
  status: Toggle,
) =>
  experiences.payload.experiences.filter(
    anyExperienceThatMatchesFilters(
      productTypeFilterStore.state.filter,
      competencyFilterStore.state.filter,
      schoolPartnerFilterStore.state.filter,
      durationFilterStore.state.filter,
      languageFilterStore.state.filter,
      status,
    ),
  );
